import React, { useCallback, useEffect, useState } from 'react';
import { Archive, LoaderCircle, MapPin, Pencil, Plus, RotateCcw, Trash2, Truck, X } from 'lucide-react';
import type { Location, SellerOrderLocation } from '../services/api.types';
import {
  createSellerOrderLocation,
  deleteSellerOrderLocation,
  fetchSellerOrderLocations,
  restoreSellerOrderLocation,
  updateSellerOrderLocation,
} from '../services/seller-order.api';
import { useNotification } from './NotificationProvider';
import { useTranslations } from '../translations';
import LocationAutocomplete from './LocationAutocomplete';
import ConfirmModal from './ConfirmModal';
import LoadingIndicator from './LoadingIndicator';
import './OrderLocationsManager.css';

const toLocation = (location: SellerOrderLocation): Location => ({
  id: location.locationId,
  name: location.locationName,
  diacriticsName: location.locationName,
  county: { id: '', name: location.countyName },
  coordinates: { latitude: 0, longitude: 0 },
  defaultLocation: false,
});

interface OrderLocationsManagerProps {
  onLocationCountChange?: (count: number) => void;
}

const OrderLocationsManager: React.FC<OrderLocationsManagerProps> = ({ onLocationCountChange }) => {
  const { showNotification } = useNotification();
  const copy = useTranslations().locations.orderLocations;
  const [locations, setLocations] = useState<SellerOrderLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<SellerOrderLocation | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [locality, setLocality] = useState<Location | null>(null);
  const [name, setName] = useState('');
  const [addressDetails, setAddressDetails] = useState('');
  const [pendingRemoval, setPendingRemoval] = useState<SellerOrderLocation | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setLocations(await fetchSellerOrderLocations());
    } catch (error) {
      console.error('Could not load order locations', error);
      showNotification(copy.loadError, 'error');
    } finally {
      setLoading(false);
    }
  }, [copy.loadError, showNotification]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!loading) onLocationCountChange?.(locations.length);
  }, [loading, locations.length, onLocationCountChange]);

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setLocality(null);
    setName('');
    setAddressDetails('');
  };

  const openCreate = () => {
    closeForm();
    setShowForm(true);
  };

  const openEdit = (location: SellerOrderLocation) => {
    setEditing(location);
    setLocality(toLocation(location));
    setName(location.name);
    setAddressDetails(location.addressDetails || '');
    setShowForm(true);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!locality || !name.trim()) {
      showNotification(copy.requiredError, 'error');
      return;
    }
    setSaving(true);
    try {
      const request = {
        locationId: locality.id,
        name: name.trim(),
        addressDetails: addressDetails.trim(),
      };
      if (editing) await updateSellerOrderLocation(editing.id, request);
      else await createSellerOrderLocation(request);
      showNotification(editing ? copy.updated : copy.created, 'success');
      closeForm();
      await load();
    } catch (error) {
      console.error('Could not save order location', error);
      showNotification(copy.saveError, 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    const location = pendingRemoval;
    if (!location) return;
    setRemoving(true);
    try {
      await deleteSellerOrderLocation(location.id);
      showNotification(location.orderCount > 0 ? copy.archivedSuccess : copy.deletedSuccess, 'success');
      setPendingRemoval(null);
      await load();
    } catch (error) {
      console.error('Could not remove order location', error);
      showNotification(copy.removeError, 'error');
      setPendingRemoval(null);
    } finally {
      setRemoving(false);
    }
  };

  const restore = async (location: SellerOrderLocation) => {
    try {
      await restoreSellerOrderLocation(location.id);
      showNotification(copy.restoredSuccess, 'success');
      await load();
    } catch (error) {
      console.error('Could not restore order location', error);
      showNotification(copy.restoreError, 'error');
    }
  };

  return (
    <section className="order-locations-manager">
      <div className="order-locations-heading">
        <div className="location-manager-heading-copy">
          <span className="location-manager-heading-icon"><Truck size={19} aria-hidden /></span>
          <div>
            <div className="location-manager-title-row">
              <h2>{copy.title}</h2>
              {!loading && <span className="location-manager-count">{locations.length}</span>}
            </div>
            <p>{copy.description}</p>
          </div>
        </div>
        <button type="button" className="order-location-add" onClick={openCreate}>
          <Plus size={18} aria-hidden /> {copy.add}
        </button>
      </div>

      {showForm && (
        <form className="order-location-form" onSubmit={save}>
          <div className="order-location-form-heading">
            <h3>{editing ? copy.editTitle : copy.createTitle}</h3>
            <button type="button" onClick={closeForm} aria-label={copy.closeForm}><X size={19} aria-hidden /></button>
          </div>
          <label>
            <span>{copy.locality}</span>
            {editing && editing.orderCount > 0 ? (
              <div className="order-location-readonly">
                <MapPin size={18} aria-hidden />{editing.locationName}, {editing.countyName}
              </div>
            ) : (
              <LocationAutocomplete
                value={locality}
                onChange={setLocality}
                placeholder={copy.localityPlaceholder}
                ariaLabel={copy.localityAria}
                required
              />
            )}
          </label>
          <label>
            <span>{copy.name}</span>
            <input value={name} maxLength={100} required placeholder={copy.namePlaceholder} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="order-location-address">
            <span>{copy.address}</span>
            <input value={addressDetails} maxLength={255} placeholder={copy.addressPlaceholder} onChange={(event) => setAddressDetails(event.target.value)} />
          </label>
          <div className="order-location-form-actions">
            <button type="button" onClick={closeForm} disabled={saving}>{copy.cancel}</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving && <LoaderCircle className="order-location-spinner" size={17} aria-hidden />}
              {saving ? copy.saving : copy.save}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <LoadingIndicator message={copy.loading} className="order-locations-state" />
      ) : locations.length === 0 ? (
        <div className="order-locations-state">{copy.empty}</div>
      ) : (
        <div className="order-location-list">
          {locations.map((location) => (
            <article className={`order-location-row${location.active ? '' : ' archived'}`} key={location.id}>
              <div className="order-location-icon"><MapPin size={19} aria-hidden /></div>
              <div className="order-location-details">
                <div className="order-location-name-row">
                  <strong>{location.name}</strong>
                  <span className={location.active ? 'active' : 'archived'}>{location.active ? copy.active : copy.archived}</span>
                </div>
                <span>{location.locationName}, {location.countyName}</span>
                {location.addressDetails && <small>{location.addressDetails}</small>}
                <small>{location.orderCount === 1 ? copy.oneOrder : copy.orderCount.replace('{count}', String(location.orderCount))}</small>
              </div>
              <div className="order-location-actions">
                {location.active ? (
                  <>
                    <button type="button" onClick={() => openEdit(location)} aria-label={`${copy.edit} ${location.name}`} title={copy.edit}><Pencil size={17} aria-hidden /></button>
                    <button type="button" className="danger" onClick={() => setPendingRemoval(location)} aria-label={`${location.orderCount > 0 ? copy.archive : copy.delete} ${location.name}`} title={location.orderCount > 0 ? copy.archive : copy.delete}>
                      {location.orderCount > 0 ? <Archive size={17} aria-hidden /> : <Trash2 size={17} aria-hidden />}
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={() => void restore(location)} aria-label={`${copy.restore} ${location.name}`} title={copy.restore}><RotateCcw size={17} aria-hidden /></button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {pendingRemoval && (
        <ConfirmModal
          title={pendingRemoval.orderCount > 0 ? copy.archiveTitle : copy.deleteTitle}
          message={pendingRemoval.orderCount > 0
            ? copy.archiveConfirm.replace('{count}', String(pendingRemoval.orderCount))
            : copy.deleteConfirm}
          confirmText={pendingRemoval.orderCount > 0 ? copy.archive : copy.delete}
          cancelText={copy.cancel}
          destructive
          isConfirming={removing}
          onConfirm={() => void confirmRemove()}
          onCancel={() => setPendingRemoval(null)}
        />
      )}
    </section>
  );
};

export default OrderLocationsManager;
